// The status bar of a two-party transaction, ported from Waffy-web-next's C2C status bar
// (components/contract/status/Status.tsx: statusConfig and subStatusConfig) for the one case the
// portal shows: the seller (PROVIDER) of a BASIC milestone contract with no shipment, outside
// Haraj. Two changes, both from WAF-3235: the payment stage reads "شارك رابط المعاملة مع المشتري
// ليراجعها ويدفع", and web-next wording the portal's own keys word differently comes from c2c_*
// copies, so the portal's status card keeps its text.
import { ECONTRACT_STATUS, EPARTY_STATUS } from "../../../../enums";
import {
  ESenderRole,
  IContractDetailsResponseData,
} from "../../../../setup/api/services/contract/models";

const SVG = "/assets/images/svg/";

export interface ISellerStatus {
  img: string;
  title: string;
  description: string;
}

export interface ISellerSubStatus {
  img: string;
  title: string;
  description: string;
  background: string;
}

const PAYMENT: ISellerStatus = {
  img: `${SVG}waiting_payment_status.svg`,
  title: "global_status_payment_title",
  description: "lbl_two_party_payment_desc",
};

/** The status bar's image, title and line (translation keys) for the seller. */
export const getSellerStatus = (
  contract: IContractDetailsResponseData
): ISellerStatus | undefined => {
  const hasEscalations = !!contract.escalations?.length;
  const pendingBankApproval = contract.parties?.some(
    (party) =>
      party?.role === ESenderRole.CUSTOMER &&
      party?.status === EPARTY_STATUS.PAYMENT_PENDING_APPROVAL
  );
  const manualWithdraw = contract.cashOutAmountList?.some(
    (cashOut) => cashOut?.manualWithDrawCashOut
  );

  switch (contract.status) {
    case ECONTRACT_STATUS.CREATED:
    case ECONTRACT_STATUS.SUBMITTED:
      return PAYMENT;
    case ECONTRACT_STATUS.PARTIALLY_PAID:
      return pendingBankApproval
        ? {
            ...PAYMENT,
            description: "status_payment_ubuyer_bank_transfer_hint_subtitle",
          }
        : PAYMENT;
    case ECONTRACT_STATUS.PENDING_PAYMENT_VERIFICATION:
      return { ...PAYMENT, description: "lbl_pending_payment_made_via_bank" };
    case ECONTRACT_STATUS.WAITING_FOR_DELIVERY:
    case ECONTRACT_STATUS.PARTIAL_CONFIRMED_DELIVERY:
      return {
        img: `${SVG}execution_status.svg`,
        title: "global_status_execution_title",
        description: "status_execution_useller_subtitle",
      };
    case ECONTRACT_STATUS.SHIPPED:
      return {
        img: `${SVG}execution_status.svg`,
        title: "global_status_shipped_title",
        description:
          "c2c_lbl_contract_details_status_execution_useller_shipping_done",
      };
    case ECONTRACT_STATUS.ITEM_INSPECTION:
    case ECONTRACT_STATUS.PARTIAL_ITEM_ACCEPTED:
      return {
        img: `${SVG}inspection_status.svg`,
        title: "global_status_inspection_title",
        description: "status_inspection_useller_subtitle",
      };
    case ECONTRACT_STATUS.ITEM_ACCEPTED:
    case ECONTRACT_STATUS.READY_FOR_CASH_OUT:
      return {
        img: `${SVG}readyForCashout_status.svg`,
        title: "c2c_global_status_settlement",
        description: "status_settlement_subtitle",
      };
    case ECONTRACT_STATUS.CASH_OUT_APPROVED:
    case ECONTRACT_STATUS.PARTIALLY_RELEASED:
      return {
        img: `${SVG}cashOutApproved_status.svg`,
        title: "c2c_global_status_transfer",
        description: manualWithdraw
          ? "status_transfer_manual_withdraw_subtitle"
          : "status_transfer_subtitle",
      };
    case ECONTRACT_STATUS.COMPLETED:
    case ECONTRACT_STATUS.EXCEPTIONAL_COMPLETED:
      return {
        img: `${SVG}${hasEscalations ? "closed_status" : "done_status"}.svg`,
        title: hasEscalations ? "global_status_closed" : "CLOSED_STATUS",
        description: "global_status_thanks",
      };
    case ECONTRACT_STATUS.DELIVERY_ESCALATION:
    case ECONTRACT_STATUS.INSPECTION_ESCALATION:
      return {
        img: `${SVG}escalation_status.svg`,
        title: "global_status_arbitration",
        description: "status_arbitration_useller_subtitle",
      };
    case ECONTRACT_STATUS.RESOLVED_REFUND_CUSTOMER:
    case ECONTRACT_STATUS.CLOSED_REFUND_CUSTOMER:
      return {
        img: `${SVG}closed_status.svg`,
        title: "global_status_closed",
        description: "status_closed_useller_for_buyer_subtitle",
      };
    case ECONTRACT_STATUS.RESOLVED_RELEASE_PROVIDER:
    case ECONTRACT_STATUS.CLOSED_RELEASE_PROVIDER:
      return {
        img: `${SVG}closed_status.svg`,
        title: "global_status_closed",
        description: "status_closed_ubuyer_for_seller_subtitle",
      };
    case ECONTRACT_STATUS.CONTRACT_ACCEPTED:
    case ECONTRACT_STATUS.READY_FOR_PAYMENT:
      return {
        img: `${SVG}depositAmount.svg`,
        title: "lbl_deposit_the_amount",
        description: "lbl_waiting_for_amount",
      };
    default:
      return undefined;
  }
};

/** The banner under the status bar, for the statuses where web-next shows one to the seller. */
export const getSellerSubStatus = (
  contract: IContractDetailsResponseData
): ISellerSubStatus | undefined => {
  switch (contract.status) {
    case ECONTRACT_STATUS.PENDING_PAYMENT_VERIFICATION:
      return {
        img: `${SVG}yellow_loader.svg`,
        title: "status_payment_useller_bank_transfer_hint_title",
        description: "status_payment_useller_bank_transfer_hint_subtitle",
        background: "#FFC1070F",
      };
    case ECONTRACT_STATUS.RESOLVED_RELEASE_PROVIDER:
      return {
        img: `${SVG}yellow_loader.svg`,
        title: "c2c_global_status_waiting_transfer_hint_title",
        description: "global_status_waiting_transfer_hint_subtitle",
        background: "#9E9E9E0F",
      };
    case ECONTRACT_STATUS.CLOSED_RELEASE_PROVIDER:
      return {
        img: `${SVG}completed_status.svg`,
        title: "global_status_transfer_done_hint_title",
        description: "global_status_transfer_done_hint_subtitle",
        background: "#9E9E9E0F",
      };
    default:
      return undefined;
  }
};

/**
 * The seller's buttons, as web-next gives them on a milestone contract outside Haraj: only
 * "مشاركة المعاملة", while the transaction waits for payment (ContractActions → ActiveContractActions
 * for CREATED / OUT_OF_STOCK / SUBMITTED). Every other stage has no seller action there.
 */
export const sellerCanShare = (contract: IContractDetailsResponseData) =>
  [
    ECONTRACT_STATUS.CREATED,
    ECONTRACT_STATUS.OUT_OF_STOCK,
    ECONTRACT_STATUS.SUBMITTED,
  ].includes(contract.status);
